/** Tiny reconnecting client. Cumulative submissions are idempotent across reconnects. */
export class Connection extends EventTarget {
  constructor(role) {
    super(); this.role = role; this.offset = 0; this.bestRTT = Infinity; this.retry = 0; this.closed = false;
    const name = role === 'player' ? 'forty-player' : 'forty-host';
    const uuid = () => crypto.randomUUID?.() || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => { const n = crypto.getRandomValues(new Uint8Array(1))[0] & 15; return (c === 'x' ? n : (n & 3) | 8).toString(16); });
    try { this.token = localStorage.getItem(name) || uuid(); localStorage.setItem(name, this.token); } catch { this.token = uuid(); }
    this.connect();
    document.addEventListener('visibilitychange', () => { if (!document.hidden) { if (this.socket?.readyState === WebSocket.OPEN) this.sync(); else if (!this.closed) this.connect(); } });
  }
  emit(type, detail) { this.dispatchEvent(new CustomEvent(type, { detail })); }
  connect() {
    if (this.socket && [WebSocket.OPEN, WebSocket.CONNECTING].includes(this.socket.readyState)) return;
    clearTimeout(this.retryTimer);
    const ws = this.socket = new WebSocket(`${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/parties/race/main`);
    ws.onopen = () => { this.retry = 0; this.bestRTT = Infinity; this.sendHello(); this.emit('connection', true); [0, 120, 300, 650].forEach(ms => setTimeout(() => this.sync(), ms)); };
    ws.onmessage = e => {
      let m; try { m = JSON.parse(e.data); } catch { return; }
      if (m.type === 'sync') {
        const rtt = Date.now() - m.sent;
        if (rtt < this.bestRTT) { this.bestRTT = rtt; this.offset = m.now - (m.sent + rtt / 2); }
      } else { if (m.type === 'welcome' && this.bestRTT === Infinity) this.offset = m.now - Date.now(); this.emit('message', m); }
    };
    ws.onclose = e => {
      this.emit('connection', false);
      if ([4001, 4002, 4004].includes(e.code)) { this.closed = true; this.emit('message', { type: 'error', message: e.reason || 'Connection closed. Reload to retry.' }); return; }
      if (!this.closed) this.retryTimer = setTimeout(() => this.connect(), Math.min(8000, 400 * 2 ** this.retry++) * (0.8 + Math.random() * 0.4));
    };
    ws.onerror = () => {};
  }
  sendHello() { this.send({ type: 'hello', role: this.role, token: this.token }); }
  sync() { this.send({ type: 'sync', sent: Date.now() }); }
  send(data) { if (this.socket?.readyState !== WebSocket.OPEN) return false; this.socket.send(JSON.stringify(data)); return true; }
  now() { return Date.now() + this.offset; }
  get online() { return this.socket?.readyState === WebSocket.OPEN; }
}

export class AudioDirector {
  constructor() { this.enabled = false; }
  async unlock() { this.ctx ||= new (window.AudioContext || window.webkitAudioContext)(); await this.ctx.resume(); this.enabled = true; }
  tone(hz = 440, seconds = 0.1, shape = 'sine', gain = 0.07) {
    if (!this.enabled || !this.ctx) return;
    const t = this.ctx.currentTime, o = this.ctx.createOscillator(), g = this.ctx.createGain();
    o.type = shape; o.frequency.setValueAtTime(hz, t); o.frequency.exponentialRampToValueAtTime(hz * 0.6, t + seconds);
    g.gain.setValueAtTime(gain, t); g.gain.exponentialRampToValueAtTime(0.001, t + seconds);
    o.connect(g).connect(this.ctx.destination); o.start(t); o.stop(t + seconds);
  }
  win() { [330, 440, 554, 660, 880].forEach((hz, i) => setTimeout(() => this.tone(hz, 0.35, 'triangle'), i * 90)); }
}
